/**
 * Reading a debug export back into something the seeding endpoint can rebuild.
 *
 * Shared by the dev panel's "Seed from export" tab and by `e2e/scripts/seed-one.ts`, which is why
 * this sits apart from `debugSnapshot.ts` — that file pulls in the stores to describe what the
 * download contains, and the e2e package can't typecheck against those.
 */
import { ArtifactName } from '@/api/generated-types';
import { ARTIFACT_FILENAMES } from '@/config/artifacts';

/** A debug export, to the extent seeding reads it. Written by `useDebugStateDownload`. */
export interface DebugExport {
  meta?: { transactionId?: string | null };
  serverArtifacts?: Record<string, unknown> | null;
}

export interface SeedableExport {
  /** The extraction the app works from. */
  document: unknown;
  /** The enum name, or the raw integer an artifact records — the API binds either. */
  documentType: string;
  layout?: unknown;
  /** Post-extraction state to replay, keyed by artifact filename. */
  overlays: Record<string, unknown>;
  /** The transaction the export came from, for labelling the copy. */
  origin: string;
}

/**
 * Artifacts that become request parts of their own rather than overlays. Everything else is
 * forwarded under its filename, and the endpoint rejects any name it doesn't seed — so an artifact
 * added to `DEBUG_ARTIFACTS` fails loudly until someone decides whether a seed should replay it.
 */
const CONSUMED: ReadonlySet<string> = new Set([
  ARTIFACT_FILENAMES[ArtifactName.Extracted],
  ARTIFACT_FILENAMES[ArtifactName.MergedResult],
  ARTIFACT_FILENAMES[ArtifactName.LayoutExtracted],
]);

/** What a replay will actually carry, for telling someone before they rely on it. */
export function replayedFilenames(seedable: SeedableExport): string[] {
  return [
    ...(seedable.layout ? [ARTIFACT_FILENAMES[ArtifactName.LayoutExtracted]] : []),
    ...Object.keys(seedable.overlays),
  ];
}

/**
 * Whether the export carries the stored verification. Called out on both surfaces because without
 * it substance verdicts start empty, and a defect in how one is read reproduces as a different bug.
 */
export function replaysVerification(seedable: SeedableExport): boolean {
  return ARTIFACT_FILENAMES[ArtifactName.SubstanceValidation] in seedable.overlays;
}

export function readSeedableExport(snapshot: DebugExport): SeedableExport {
  const artifacts = snapshot.serverArtifacts ?? {};

  // Either: extraction writes one object to both names, so they are the same bytes. Merged is
  // only present when the layout stage ran.
  const document =
    artifacts[ARTIFACT_FILENAMES[ArtifactName.MergedResult]] ??
    artifacts[ARTIFACT_FILENAMES[ArtifactName.Extracted]];
  if (document == null) {
    throw new Error(
      'This export carries no extraction. It was either taken before the transaction finished ' +
        'extracting, or it is not a debug export.'
    );
  }

  return {
    document,
    documentType: String((document as { documentType?: unknown }).documentType ?? 'Dgd'),
    layout: artifacts[ARTIFACT_FILENAMES[ArtifactName.LayoutExtracted]] ?? undefined,
    overlays: Object.fromEntries(
      Object.entries(artifacts).filter(
        ([filename, value]) => value != null && !CONSUMED.has(filename)
      )
    ),
    origin: snapshot.meta?.transactionId ?? 'unknown',
  };
}
